import React, { useState } from "react";
import createPlotlyComponent from "react-plotly.js/factory";
import Plotly from "plotly.js-dist-min";
import BasePlot from "./BasePlot";

const Plot = createPlotlyComponent(Plotly);

export interface ScatterPlotProps {
  data: any[];
  xMetric?: string;
  yMetric?: string;
  title?: string;
  defaultColor?: string;
  defaultSize?: number;
  className?: string;
  plotRef?: string;
  isFullscreen?: boolean;
}

const ScatterPlot = ({
  data,
  xMetric = "total_counts",
  yMetric = "n_genes_by_counts",
  title = "Scatter Plot",
  defaultColor = "#EF553B",
  defaultSize = 8,
  className = "",
  plotRef,
  isFullscreen = false,
}: ScatterPlotProps): React.ReactElement => {
  const [color, setColor] = useState(defaultColor);
  const [markerSize, setMarkerSize] = useState(defaultSize);

  const xData = data.map((d) => d[xMetric]);
  const yData = data.map((d) => d[yMetric]);

  const plotComponent = (
    <Plot
      data={[
        {
          type: "scattergl",
          mode: "markers",
          x: xData,
          y: yData,
          marker: {
            color,
            size: markerSize,
            opacity: 0.7,
          },
          name: `${yMetric} vs ${xMetric}`,
        },
      ]}
      layout={{
        title: {
          text: `${title}: ${yMetric} vs ${xMetric}`,
          font: { size: 14 },
        },
        height: undefined, // Let container control height for proper filling
        autosize: true,
        uirevision: "survom-scatter",
        paper_bgcolor: "white",
        plot_bgcolor: "white",
        margin: { l: 70, r: 40, t: 40, b: 60 }, // Adequate margins to prevent label cutoff
        font: { size: 12 },
        xaxis: {
          title: { text: xMetric, font: { size: 12 } },
          tickfont: { size: 10 },
        },
        yaxis: {
          title: { text: yMetric, font: { size: 12 } },
          tickfont: { size: 10 },
        },
      }}
      config={{
        displayModeBar: false,
        responsive: true,
      }}
      style={{ width: "100%", height: "100%" }}
      className={plotRef ? plotRef.replace("#", "") : "plotly-plot"}
      useResizeHandler={true}
    />
  );

  return (
    <BasePlot
      title={title}
      className={className}
      showColorPicker={true}
      defaultColor={defaultColor}
      onColorChange={setColor}
      showSizeSlider={true}
      defaultSize={defaultSize}
      onSizeChange={setMarkerSize}
      downloadFilename={`${xMetric}_vs_${yMetric}_scatter`}
      plotType="scatter"
      plotProps={{
        data,
        xMetric,
        yMetric,
      }}
      isFullscreen={isFullscreen}
    >
      {plotComponent}
    </BasePlot>
  );
};

export default ScatterPlot;
